<template>
  <ServiceCentersLayout>
    <template #menu>
      <div class="space-y-4">
        <UiSelect
          v-model="selectedCountry"
          :options="countries"
          :placeholder="$t('choose_country')"
          item-text="name"
        />
        <UiSelect
          v-model="selectedCity"
          :disabled="citiesSelectDisable"
          :options="cities"
          :placeholder="$t('choose_city')"
          clearable
          item-text="name"
        />
      </div>
    </template>
    <template #list>
      <ServiceCentersList>
        <ServiceCentersListItem
          v-for="(service, i) in filteredServiceCenters"
          :key="i"
          :name="service.name"
          :url="service.url"
          :address="service.address"
          :phones="service.phones"
          :working-hours="service.working_hours"
          :is-active="activeMarker === i"
          @click="setActiveMarker(i)"
        />
      </ServiceCentersList>
    </template>
    <template #map>
      <client-only>
        <UiMap
          :markers="markers"
          :active-marker="activeMarker"
          @update:active-marker="setActiveMarker"
        />
      </client-only>
    </template>
    <template #map-card>
      <ServiceCentersListItem
        v-if="activeService"
        :name="activeService.name"
        :url="activeService.url"
        :address="activeService.address"
        :phones="activeService.phones"
        :working-hours="activeService.working_hours"
      />
    </template>
  </ServiceCentersLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import {
  useCitiesByIds,
  useCountriesByIds,
  useServiceCentersCitiesIdsByCountryId,
  useServiceCentersCountriesIds,
} from '@webplatform/frontkit/composables';
import type { AppCity, AppCountry } from '@webplatform/frontkit/runtime/types';

// TODO(AB-2221): после добавления url и working_hours в сериализатор
// useServiceCenters брать центры из него вместо прямого вызова
// serviceCenterService и разворачивания addresses в serviceCenters.
const {
  $api: { serviceCenterService },
} = useNuxtApp();

const countries = ref<AppCountry[]>([]);
const cities = ref<AppCity[]>([]);
const centers = ref<any[]>([]);
const countriesIds = ref<number[]>([]);
const citiesIds = ref<number[]>([]);
const selectedCountry = ref<any>(null);
const selectedCity = ref<any>(null);
const activeMarker = ref<any>(null);
const citiesLoading = ref(false);
const setActiveMarker = (i: any) => {
  activeMarker.value = i;
};

const selectedCountryId = computed(() => selectedCountry.value);

const { fetch: fetchServiceCentersCountriesIds } =
  useServiceCentersCountriesIds();

const { fetch: fetchServiceCentersCitiesIds } =
  useServiceCentersCitiesIdsByCountryId(selectedCountryId);

// TODO(AB-2221): после того как во фронтките можно будет задавать limit,
// заменить пары ...Ids + use...ByIds на useServiceCentersCountries и
// useServiceCentersCitiesByCountryId, убрать countriesIds и citiesIds.
const { fetch: fetchCountriesByIds } = useCountriesByIds(
  computed(() => ({ country_id: countriesIds.value, limit: 1000 })),
);

const { fetch: fetchCitiesByIds } = useCitiesByIds(
  computed(() => ({ city_id: citiesIds.value, limit: 1000 })),
);

const fetchServiceCenters = async () => {
  try {
    const { data } = await serviceCenterService.getAllServiceCenters({
      limit: 9999,
    });
    centers.value = data;
  } catch (e) {
    console.error('Fetch service centers failed: ', e);
    centers.value = [];
  }
};

const fetchCountries = async () => {
  const { countriesIds: ids } = await fetchServiceCentersCountriesIds();
  countriesIds.value = ids.map((country) => country.id);
  countries.value = countriesIds.value.length
    ? await fetchCountriesByIds()
    : [];
};

const fetchCities = async () => {
  try {
    citiesLoading.value = true;
    const { citiesIds: ids } = await fetchServiceCentersCitiesIds();
    citiesIds.value = ids.map((city) => city.id);
    cities.value = citiesIds.value.length ? await fetchCitiesByIds() : [];
  } finally {
    citiesLoading.value = false;
  }
};

const filterHours = (hours: any) => {
  return hours.filter((hour: any) => hour.start !== null && hour.end !== null);
};

const serviceCenters = computed(() => {
  const result: any[] = [];
  centers.value.forEach((s) => {
    s.addresses.forEach((address: any) => {
      result.push({
        name: s.name,
        url: address.url,
        address: address.address,
        phones: address.phones,
        lat: Number(address.lat),
        lng: Number(address.lon),
        countryId: address.country_id,
        cityId: address.city_id,
        working_hours: filterHours(address.working_hours),
      });
    });
  });

  return result;
});

const citiesSelectDisable = computed(
  () =>
    selectedCountry.value === null ||
    citiesLoading.value ||
    (!citiesLoading.value && !cities.value.length),
);

const filteredServiceCenters = computed(() => {
  let result = [];
  if (selectedCountry.value !== null && !selectedCity.value) {
    result = serviceCenters.value.filter(
      (s) => selectedCountry.value === s.countryId,
    );
  } else if (selectedCity.value !== null) {
    result = serviceCenters.value.filter(
      (s) => selectedCity.value === s.cityId,
    );
  } else {
    result = serviceCenters.value;
  }
  return result;
});

const activeService = computed(
  () => filteredServiceCenters.value[activeMarker.value],
);

const markers = computed(() =>
  filteredServiceCenters.value.map((s) => {
    return {
      title: s.name,
      position: {
        lat: s.lat,
        lng: s.lng,
      },
    };
  }),
);

watch(selectedCountry, () => {
  selectedCity.value = null;
  activeMarker.value = null;
  fetchCities();
});

watch(selectedCity, () => {
  activeMarker.value = null;
});

onMounted(() => {
  fetchServiceCenters();
  fetchCountries();
});
</script>
